import { QueryFilterGroupBy } from '../../../shared/state/query-filter.functions';
import { get } from 'dot-prop';
import { Position } from './position.model';
import { isArray } from '@datorama/akita';
import { Currency } from '../../../shared/components/currency-selector/currency-country-codes';

/** @deprecated accumulatePositionsByGroup - use generic accumulateByGroup instead
 * @param positions - positions to accumulate
 * @param accumulatorFunction - function used to accumulate positions
 * @param groupBy - optionally group positions by a key e.g. 'underlyingInstrumentId'
 * @returns Observable<Array<R>> - array of accumulated positions
 */
export const accumulatePositionsByGroup = <R>(
    positions: Array<Position>,
    accumulatorFunction: (
        position: Position,
        previous: R,
        index: number,
        subgroup?: QueryFilterGroupBy
    ) => R,
    groupBy?: QueryFilterGroupBy | Array<QueryFilterGroupBy>
) => {
    if (groupBy && !isArray(groupBy)) {
        // create placeholder for groups
        const GROUPS: { [group: string | number]: R } = {};
        for (let i = 0; i < positions.length; i++) {
            const POSITION = positions[i];
            let group = get<string | number>(positions[i], groupBy.key);
            // if groupBy.valueModifier is provided...
            if (groupBy.valueModifier) {
                // ...use it to modify the group value
                group = groupBy.valueModifier(group);
            }
            // add group
            GROUPS[group] = accumulatorFunction(
                POSITION,
                GROUPS[group],
                i,
                groupBy.subgroup
            );
        }
        if (groupBy.subgroup) {
            Object.entries(GROUPS).forEach(([key, value]) => {
                const subgroups = accumulatePositionsByGroup(
                    // @ts-ignore
                    value.positions,
                    accumulatorFunction,
                    groupBy.subgroup
                );
                GROUPS[key] = { ...GROUPS[key], subgroups };
            });
        }
        return GROUPS;
    } else if (groupBy && isArray(groupBy)) {
        // create placeholder for groups
        const GROUPS: { [group: string | number]: R } = groupBy.reduce(
            (acc, curr) => ({ ...acc, [curr.name]: {} }),
            {}
        );
        for (let i = 0; i < positions.length; i++) {
            const POSITION = positions[i];
            groupBy.forEach((group) => {
                let groupValue = get<string | number>(positions[i], group.key);
                // if groupBy.valueModifier is provided...
                if (group.valueModifier) {
                    // ...use it to modify the group value
                    groupValue = group.valueModifier(groupValue);
                }
                // add group
                GROUPS[group.name][groupValue] = accumulatorFunction(
                    POSITION,
                    GROUPS[group.name][groupValue],
                    i,
                    group.subgroup
                );
            });
        }

        groupBy
            .filter((group) => group.subgroup)
            .forEach((group) => {
                Object.entries(GROUPS[group.name]).forEach(([key, value]) => {
                    const subgroups = accumulatePositionsByGroup(
                        value.positions,
                        accumulatorFunction,
                        group.subgroup
                    );
                    GROUPS[group.name][key] = { ...GROUPS[group.name][key], subgroups };
                });
            });
        return GROUPS;
    } else {
        let accumulated: R = null;
        for (let i = 0; i < positions.length; i++) {
            const POSITION = positions[i];
            accumulated = accumulatorFunction(POSITION, accumulated, i);
        }
        return accumulated;
    }
};

/** getTotals - calculate totals for passed positions
 * @param positions
 * @param currenciesAndRates
 */
export const getTotals = (
    positions: Array<Position>,
    currenciesAndRates: { [currency in Currency]: number }
) => {
    const totals = {
        marketValueInBaseCurrency: 0,
        acquisitionValueInBaseCurrency: 0,
        totalPnlInBaseCurrency: 0,
        entities: []
    };
    for (let i = 0; i < positions?.length; i++) {
        const position = positions[i];
        const conversionRate = currenciesAndRates[position.position.currency];
        totals.marketValueInBaseCurrency += position.marketValueInBaseCurrency;
        totals.acquisitionValueInBaseCurrency +=
            position.acquisitionValue * conversionRate;
        totals.totalPnlInBaseCurrency +=
            ((position.position.realizedPnLAvg ?? 0) + (position.unrealizedPnLAvg ?? 0)) *
            conversionRate;
        totals.entities.push(positions[i]);
    }
    return totals;
};
